import { useSearchParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { BRAND } from "@/lib/site";
import { ShopGrid, useListings, type ShopItem } from "@/components/site/ShopGrid";

const TYPES: Record<string, string> = { "gift-kit": "Gift Kits", cards: "Cards & Keepsakes", "tags-labels": "Tags & Labels", packaging: "Gift Packaging",
  coloring: "Coloring", "craft-template": "Craft Templates", bundle: "Bundles" };
const OCCASIONS: Record<string, string> = { christmas: "Christmas", fall: "Fall & Halloween", everyday: "Everyday & Birthdays" };
const COLLECTIONS: Record<string, string> = { "classic-christmas": "Classic", "whimsical-christmas": "Whimsical", "nordic-christmas": "Nordic", "retro-christmas": "Retro",
  "elegant-christmas": "Elegant", "woodland-christmas": "Woodland", "childs-eyes-christmas": "Through a Child's Eyes" };

function Chips({ label, k, opts, items, params, set }: { label: string; k: string; opts: Record<string, string>; items: ShopItem[]; params: URLSearchParams; set: (p: URLSearchParams) => void }) {
  const field = (i: ShopItem) => (k === "type" ? i.product_type : k === "occasion" ? i.season : i.collection) || "";
  const present = Object.keys(opts).filter((o) => items.some((i) => field(i) === o));
  if (present.length < 2) return null;
  const cur = params.get(k) || "";
  const pick = (v: string) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); set(p); };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`shop-filter-group-${k}`}>
      <span className="mr-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#23324A]/55">{label}</span>
      {["", ...present].map((o) => (
        <button key={o || "all"} onClick={() => pick(o)} className={`tab ${cur === o ? "tab-active" : ""}`} data-testid={`shop-filter-${k}-${o || "all"}`}>{o ? opts[o] : "All"}</button>
      ))}
    </div>
  );
}

export default function ShopPage() {
  usePageMeta(`Shop printable gift kits | ${BRAND}`, "Printable gift kits, cards, tags and labels for holidays, teachers and family. Instant download, US Letter and A4.");
  const [params, setParams] = useSearchParams();
  const items = useListings() || [];
  const t = params.get("type"), o = params.get("occasion"), c = params.get("collection");
  const shown = items.filter((i) => (!t || i.product_type === t) && (!o || i.season === o) && (!c || i.collection === c));
  return (
    <div className="mx-auto max-w-6xl px-5 pt-14 sm:px-8" data-testid="shop-page">
      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-[#23324A]/55">Shop</p>
      <h1 className="font-heading text-4xl font-semibold text-[#23324A] sm:text-5xl lg:text-6xl">Printable gift kits</h1>
      <p className="mt-4 max-w-xl text-[#23324A]/75">Every kit is an instant download in US Letter and A4. Buy right here, download instantly, print at home, and finish it by hand.</p>
      <div className="mt-8 space-y-3" data-testid="shop-filters">
        <Chips label="Type" k="type" opts={TYPES} items={items} params={params} set={setParams} />
        <Chips label="Occasion" k="occasion" opts={OCCASIONS} items={items} params={params} set={setParams} />
        <Chips label="Collection" k="collection" opts={COLLECTIONS} items={items} params={params} set={setParams} />
      </div>
      {items.length > 0 && shown.length === 0 && <p className="mt-10 text-[#23324A]/70" data-testid="shop-empty">No printables match those filters yet.</p>}
      <div className="mt-10"><ShopGrid items={shown} /></div>
    </div>
  );
}
